import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * Apple-style parallax section
 * Elements fade in and scale as they enter viewport
 */
export const ParallaxSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.8, 1, 1, 0.8]);
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <motion.div
      ref={ref}
      style={{ opacity, scale, y }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Apple-style reveal on scroll
 * Smooth fade-up animation
 */
export const RevealOnScroll = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "start 0.5"]
  });

  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [60, 0]);

  return (
    <motion.div
      ref={ref}
      style={{ opacity, y }}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Apple-style sticky reveal
 * Element sticks while revealing content
 */
export const StickyReveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.05, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1, 0]);

  return (
    <div ref={ref} className="relative">
      <motion.div
        style={{ scale, opacity }}
        className={`sticky top-20 ${className}`}
      >
        {children}
      </motion.div>
    </div>
  );
};

/**
 * Apple-style zoom on scroll
 * Image or content zooms in as you scroll
 */
export const ZoomOnScroll = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.1, 1.2]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ scale }} className="w-full h-full">
        {children}
      </motion.div>
    </div>
  );
};

/**
 * Apple-style clip reveal
 * Content reveals with a clip-path animation
 */
export const ClipReveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.8", "start 0.2"]
  });

  const clipPath = useTransform(
    scrollYProgress,
    [0, 1],
    ["inset(0% 0% 100% 0%)", "inset(0% 0% 0% 0%)"]
  );

  return (
    <div ref={ref} className={className}>
      <motion.div style={{ clipPath }}>
        {children}
      </motion.div>
    </div>
  );
};

/**
 * Apple-style text reveal
 * Words appear one by one
 */
export const TextReveal = ({ text, className = '' }) => {
  const words = text.split(' ');

  return (
    <div className={className}>
      {words.map((word, index) => (
        <motion.span
          key={index}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{
            duration: 0.5,
            delay: index * 0.05,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          className="inline-block mr-2"
        >
          {word}
        </motion.span>
      ))}
    </div>
  );
};

/**
 * Apple-style fade slide
 * Smooth horizontal slide with fade
 */
export const FadeSlide = ({ children, direction = 'left', className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "start 0.5"]
  });

  const x = useTransform(
    scrollYProgress,
    [0, 1],
    direction === 'left' ? [-100, 0] : [100, 0]
  );
  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <motion.div
      ref={ref}
      style={{ x, opacity }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Apple-style card stack
 * Cards stack on top of each other with parallax
 */
export const CardStack = ({ children, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.9, 1], [0, 1, 1, 0.5]);

  return (
    <motion.div
      ref={ref}
      style={{ y, scale, opacity }}
      className={`sticky top-32 ${className}`}
    >
      {children}
    </motion.div>
  );
};

/**
 * Apple-style number counter
 * Animates numbers as they appear
 */
export const AnimatedNumber = ({ value, duration = 2, className = '' }) => {
  return (
    <motion.span
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className={className}
    >
      <motion.span
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration }}
        viewport={{ once: true }}
      >
        {value}
      </motion.span>
    </motion.span>
  );
};

export default {
  ParallaxSection,
  RevealOnScroll,
  StickyReveal,
  ZoomOnScroll,
  ClipReveal,
  TextReveal,
  FadeSlide,
  CardStack,
  AnimatedNumber
};
